import React, { useState, useEffect } from 'react';
import { Plus, Edit, ChevronLeft, ChevronRight, ArrowLeft, Check, ChevronUp, ChevronDown, Download } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import apiService from '../../utils/api';
import { useToast } from '../../components/Toast';
import BulkSelectBar, { BulkSelectToggle } from '../../components/BulkSelectBar';
import ConfirmDialog from '../../components/ConfirmDialog';
import ExportModal from '../../components/ExportModal';
import { sortedByName } from '../../utils/sortAlpha';
import { downloadCsv, fetchAllPages } from '../../utils/exportCsv';
const TODAY_DATE = new Date().toISOString().split('T')[0];

function PaymentHistoryPage() {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerInvoices, setCustomerInvoices] = useState([]); // invoices for selected customer
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentView, setCurrentView] = useState('list');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '', invoice_id: '', amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'cash', cash_amount: '', bank_amount: '',
    bank_charges: '',
    transaction_id: '', reference_number: '', notes: '', status: 'completed'
  });
  const [customerCredit, setCustomerCredit] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 30;

  const [filters, setFilters] = useState({
    customer_id: '', start_date: '', end_date: '', payment_mode: '', status: ''
  });
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc' — by date

  // bulk select / delete
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // { paymentIds, count }

  // export
  const [showExport, setShowExport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    customer_id: '', start_date: '', end_date: '', payment_mode: '', status: ''
  });

  const loadPayments = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      let allPayments = [];
      let totalPayments = 0;

      if (filters.customer_id) {
        const params = { page, per_page: itemsPerPage, start_date: filters.start_date, end_date: filters.end_date, sort_order: sortOrder };
        const response = await apiService.getCustomerPayments(filters.customer_id, params);
        allPayments = response.data || [];
        totalPayments = response.pagination?.total || 0;
        setCurrentPage(response.pagination?.page || 1);
        setTotalPages(response.pagination?.pages || 1);
      } else {
        const customersResponse = await apiService.getCustomers();
        const list = customersResponse.data || [];
        const arrays = await Promise.all(list.map(async c => {
          try {
            const r = await apiService.getCustomerPayments(c.customer_id, { per_page: 100, start_date: filters.start_date, end_date: filters.end_date });
            return (r.data || []).map(p => ({ ...p, customer_name: c.name }));
          } catch { return []; }
        }));
        let combined = arrays.flat();
        if (filters.payment_mode) combined = combined.filter(p => p.payment_mode === filters.payment_mode);
        if (filters.status) combined = combined.filter(p => p.status === filters.status);
        combined.sort((a, b) => sortOrder === 'desc'
          ? new Date(b.payment_date) - new Date(a.payment_date)
          : new Date(a.payment_date) - new Date(b.payment_date));
        totalPayments = combined.length;
        allPayments = combined.slice((page - 1) * itemsPerPage, page * itemsPerPage);
        setCurrentPage(page);
        setTotalPages(Math.ceil(totalPayments / itemsPerPage));
      }
      setData(allPayments);
      setTotalItems(totalPayments);
    } catch (err) {
      setError('Failed to load payment history: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    const response = await apiService.getCustomers();
    setCustomers(response.data || []);
  };

  const loadCustomerInvoices = async (customerId) => {
    if (!customerId) { setCustomerInvoices([]); return; }
    try {
      const response = await apiService.getInvoices({ customer_id: customerId });
      // Show only sent / partial / overdue invoices (not fully paid)
      const open = (response.data || []).filter(inv => inv.status !== 'paid');
      setCustomerInvoices(open);
    } catch { setCustomerInvoices([]); }
  };

  useEffect(() => { loadCustomers(); loadPayments(); }, []);
  useEffect(() => { loadPayments(1); }, [filters, sortOrder]);

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({ customer_id: '', invoice_id: '', amount: '', payment_date: new Date().toISOString().split('T')[0], payment_mode: 'cash', cash_amount: '', bank_amount: '', bank_charges: '', transaction_id: '', reference_number: '', notes: '', status: 'completed' });
    setCustomerInvoices([]);
    setCustomerCredit(null);
    setCurrentView('form');
  };

  const loadCustomerCredit = async (customerId) => {
    if (!customerId) { setCustomerCredit(null); return; }
    try {
      const r = await apiService.getCustomerCredit(customerId);
      setCustomerCredit(r.data?.available ?? 0);
    } catch { setCustomerCredit(null); }
  };

  const handleEdit = async (item) => {
    setEditingItem(item);
    setFormData({
      customer_id: item.customer_id?.toString() || '',
      invoice_id: item.invoice_id?.toString() || '',
      amount: item.amount?.toString() || '',
      payment_date: item.payment_date || new Date().toISOString().split('T')[0],
      payment_mode: item.payment_mode || 'cash',
      cash_amount: item.cash_amount?.toString() || '',
      bank_amount: (item.bank_amount ?? item.online_amount ?? '').toString(),
      bank_charges: (item.bank_charges ?? '').toString(),
      transaction_id: item.transaction_id || '',
      reference_number: item.reference_number || '',
      notes: item.notes || '',
      status: item.status || 'completed'
    });
    await loadCustomerInvoices(item.customer_id);
    await loadCustomerCredit(item.customer_id);
    setCurrentView('form');
  };

  /* ── bulk select / delete ────────────────────────────────────────────── */
  const rowKey = (item) => `${item.customer_id}:${item.payment_id}`;

  const enterSelectMode = () => { setSelectMode(true); setSelectedIds(new Set()); };
  const exitSelectMode = () => { setSelectMode(false); setSelectedIds(new Set()); };

  const toggleRow = (key) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const allSelected = data.length > 0 && data.every(item => selectedIds.has(rowKey(item)));
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(data.map(rowKey)));
  };

  const requestBulkDelete = () => {
    if (selectedIds.size === 0) return;
    const paymentIds = data.filter(item => selectedIds.has(rowKey(item))).map(item => item.payment_id);
    setConfirmDelete({ paymentIds, count: paymentIds.length });
  };

  const confirmBulkDelete = async () => {
    if (!confirmDelete) return;
    try {
      setBulkDeleting(true);
      await apiService.bulkDeletePayments(confirmDelete.paymentIds);
      toast.success(`${confirmDelete.count} payment record(s) deleted successfully`);
      setConfirmDelete(null);
      exitSelectMode();
      await loadPayments(currentPage);
    } catch (err) {
      toast.error('Failed to delete: ' + err.message);
    } finally {
      setBulkDeleting(false);
    }
  };

  /* ── export ──────────────────────────────────────────────────────────── */
  const openExport = () => {
    setExportFilters({ ...filters });
    setShowExport(true);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      let rows;

      if (exportFilters.customer_id) {
        rows = await fetchAllPages((page, per_page) => apiService.getCustomerPayments(exportFilters.customer_id, {
          page, per_page, start_date: exportFilters.start_date, end_date: exportFilters.end_date
        }));
      } else {
        const customersResponse = await apiService.getCustomers();
        const list = customersResponse.data || [];
        const arrays = await Promise.all(list.map(async c => {
          const r = await fetchAllPages((page, per_page) => apiService.getCustomerPayments(c.customer_id, {
            page, per_page, start_date: exportFilters.start_date, end_date: exportFilters.end_date
          }));
          return r.map(p => ({ ...p, customer_name: c.name }));
        }));
        rows = arrays.flat();
      }

      if (exportFilters.payment_mode) rows = rows.filter(p => p.payment_mode === exportFilters.payment_mode);
      if (exportFilters.status) rows = rows.filter(p => p.status === exportFilters.status);
      rows.sort((a, b) => new Date(a.payment_date) - new Date(b.payment_date));

      const columns = [
        { header: 'ID', key: 'payment_id' },
        { header: 'Date', value: r => formatDate(r.payment_date) },
        { header: 'Customer', value: r => r.customer_name || `Customer ${r.customer_id}` },
        { header: 'Invoice No', value: r => r.invoice_number || '' },
        { header: 'Amount', value: r => (r.amount || 0).toFixed(2) },
        { header: 'Mode', key: 'payment_mode' },
        { header: 'Txn / Ref', value: r => r.transaction_id || r.reference_number || '' },
        { header: 'Status', value: r => r.status || 'completed' },
      ];

      downloadCsv(`receipts_${new Date().toISOString().slice(0, 10)}.csv`, columns, rows);
      toast.success(`Exported ${rows.length} record(s).`);
      setShowExport(false);
    } catch (err) {
      toast.error('Failed to export: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.customer_id) { toast.warning('Please select a customer.'); return; }
    if (!formData.amount || Number(formData.amount) <= 0) { toast.warning('Please enter a valid amount.'); return; }
    if (!formData.payment_date) { toast.warning('Please select a payment date.'); return; }

    const totalAmount = Number(formData.amount);
    const cashAmount = formData.cash_amount ? Number(formData.cash_amount) : 0;
    const bankAmount = formData.bank_amount ? Number(formData.bank_amount) : 0;
    if (formData.payment_mode === 'both' && Math.abs((cashAmount + bankAmount) - totalAmount) > 0.01) {
      toast.warning('Cash + Bank must equal total amount when mode is "Both".');
      return;
    }

    // Surplus warning: payment exceeds invoice balance → will be saved as advance credit
    if (formData.invoice_id) {
      const inv = customerInvoices.find(i => i.invoice_id.toString() === formData.invoice_id.toString());
      if (inv) {
        const excludeOwn = (editingItem && editingItem.invoice_id === inv.invoice_id) ? Number(editingItem.amount || 0) : 0;
        const remaining = (inv.balance || 0) + excludeOwn;
        if (totalAmount > remaining + 0.01) {
          const surplus = (totalAmount - remaining).toFixed(2);
          if (!window.confirm(`₹${surplus} will be saved as advance / surplus credit for this customer. Continue?`)) return;
        }
      }
    }

    const payload = {
      amount: totalAmount,
      payment_date: formData.payment_date,
      payment_mode: formData.payment_mode,
      cash_amount: formData.payment_mode === 'cash' ? totalAmount : (formData.payment_mode === 'both' ? cashAmount : 0),
      bank_amount: (formData.payment_mode === 'bank' || formData.payment_mode === 'upi') ? totalAmount : (formData.payment_mode === 'both' ? bankAmount : 0),
      bank_charges: (formData.payment_mode !== 'cash' && formData.bank_charges) ? Number(formData.bank_charges) : 0,
      transaction_id: formData.transaction_id.trim() || null,
      reference_number: formData.reference_number.trim() || null,
      notes: formData.notes.trim() || null,
      status: formData.status,
      invoice_id: formData.invoice_id ? parseInt(formData.invoice_id) : null
    };

    try {
      setLoading(true);
      setError(null);
      if (editingItem) {
        await apiService.updateCustomerPayment(editingItem.customer_id, editingItem.payment_id, payload);
      } else {
        await apiService.addCustomerPayment(formData.customer_id, payload);
      }
      await loadPayments(currentPage);
      setCurrentView('list');
    } catch (err) {
      toast.error('Failed to save payment: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'customer_id') {
        next.invoice_id = '';
        loadCustomerInvoices(value);
        loadCustomerCredit(value);
      }
      if (name === 'payment_mode') {
        if (value === 'cash') { next.cash_amount = prev.amount; next.bank_amount = ''; next.bank_charges = ''; }
        else if (value === 'bank' || value === 'upi') { next.bank_amount = prev.amount; next.cash_amount = ''; }
        else { next.cash_amount = ''; next.bank_amount = ''; }
      }
      if (name === 'amount') {
        if (prev.payment_mode === 'cash') next.cash_amount = value;
        else if (prev.payment_mode === 'bank' || prev.payment_mode === 'upi') next.bank_amount = value;
      }
      return next;
    });
  };

  const formatCurrency = (v) => `₹${parseFloat(v || 0).toFixed(2)}`;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : 'N/A';

  const renderLayout = (content) => (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">{content}</div>
      </div>
    </div>
  );

  if (loading && data.length === 0) {
    return renderLayout(<div className="loading-container"><div className="spinner"></div><p>Loading...</p></div>);
  }

  if (currentView === 'form') {
    return renderLayout(
      <>
        <div className="page-header">
          <div className="header-left">
            <button className="btn btn-secondary" onClick={() => setCurrentView('list')} style={{ marginRight: '1rem' }}><ArrowLeft size={18} /> Cancel</button>
            <h1 className="page-title">{editingItem ? 'Edit Payment' : 'Add Receipt'}</h1>
          </div>
        </div>

        {error && <div className="error-container"><p>{error}</p></div>}

        <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Customer *</label>
              <select name="customer_id" className="form-select" value={formData.customer_id} onChange={handleInputChange} disabled={loading || !!editingItem}>
                <option value="">Select Customer</option>
                {sortedByName(customers).map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Payment Date *</label>
              <input type="date" max={TODAY_DATE} name="payment_date" className="form-input" value={formData.payment_date} onChange={handleInputChange} disabled={loading} />
            </div>
          </div>

          {formData.customer_id && customerCredit != null && customerCredit > 0.01 && (
            <div style={{ padding: '0.7rem 1rem', background: 'var(--success-bg, #ecfdf5)', borderLeft: '4px solid var(--success-color)', borderRadius: 'var(--radius-md)', marginBottom: '1rem', fontSize: '0.9rem' }}>
              💰 This customer has <strong>₹{customerCredit.toFixed(2)}</strong> available advance credit (auto-applies to next bill).
            </div>
          )}

          {formData.customer_id && (
            <div className="form-group">
              <label className="form-label">Against Invoice (Bill No)</label>
              <select name="invoice_id" className="form-select" value={formData.invoice_id} onChange={handleInputChange} disabled={loading}>
                <option value="">-- Select Invoice (optional) --</option>
                {sortedByName(customerInvoices, inv => inv.invoice_number).map(inv => (
                  <option key={inv.invoice_id} value={inv.invoice_id}>
                    {inv.invoice_number} | {formatDate(inv.invoice_date)} | ₹{(inv.total_amount || 0).toFixed(2)} | Balance: ₹{(inv.balance || 0).toFixed(2)}
                  </option>
                ))}
              </select>
              {customerInvoices.length === 0 && formData.customer_id && (
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.3rem' }}>No open invoices found for this customer.</div>
              )}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Total Amount *</label>
              <input type="number" name="amount" className="form-input" value={formData.amount} onChange={handleInputChange} placeholder="Enter amount" min="0" step="0.01" disabled={loading} />
              {formData.invoice_id && (() => {
                const inv = customerInvoices.find(i => i.invoice_id.toString() === formData.invoice_id.toString());
                if (!inv) return null;
                const excludeOwn = (editingItem && editingItem.invoice_id === inv.invoice_id) ? Number(editingItem.amount || 0) : 0;
                const remaining = (inv.balance || 0) + excludeOwn;
                const total = Number(formData.amount || 0);
                const overflow = total - remaining;
                return (
                  <small style={{ color: overflow > 0.01 ? 'var(--warning-color)' : 'var(--text-secondary)' }}>
                    Invoice balance: ₹{remaining.toFixed(2)}
                    {overflow > 0.01 && <> &middot; ₹{overflow.toFixed(2)} will go to advance credit</>}
                  </small>
                );
              })()}
              {customerCredit != null && customerCredit > 0.01 && (
                <small style={{ color: 'var(--success-color)', display: 'block' }}>
                  Available advance credit: ₹{customerCredit.toFixed(2)}
                </small>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Payment Mode *</label>
              <select name="payment_mode" className="form-select" value={formData.payment_mode} onChange={handleInputChange} disabled={loading}>
                <option value="cash">Cash</option>
                <option value="bank">Bank</option>
                <option value="upi">UPI</option>
                <option value="both">Both (Cash + Bank)</option>
              </select>
            </div>
          </div>

          {formData.payment_mode === 'both' && (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Cash Portion</label>
                <input type="number" name="cash_amount" className="form-input" value={formData.cash_amount} onChange={handleInputChange} min="0" step="0.01" disabled={loading} />
              </div>
              <div className="form-group">
                <label className="form-label">Bank Portion</label>
                <input type="number" name="bank_amount" className="form-input" value={formData.bank_amount} onChange={handleInputChange} min="0" step="0.01" disabled={loading} />
              </div>
            </div>
          )}

          {formData.payment_mode !== 'cash' && (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Bank Charges (optional)</label>
                <input type="number" name="bank_charges" className="form-input" value={formData.bank_charges} onChange={handleInputChange} placeholder="0.00" min="0" step="0.01" disabled={loading} />
                <small style={{ color: 'var(--text-secondary)' }}>Extra charges deducted by bank (RTGS/IMPS fees, etc.). Leave blank if none.</small>
              </div>
              <div className="form-group">
                <label className="form-label">Transaction ID</label>
                <input type="text" name="transaction_id" className="form-input" value={formData.transaction_id} onChange={handleInputChange} placeholder="Transaction ID" disabled={loading} />
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Reference Number</label>
              <input type="text" name="reference_number" className="form-input" value={formData.reference_number} onChange={handleInputChange} placeholder="Optional reference" disabled={loading} />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select name="status" className="form-select" value={formData.status} onChange={handleInputChange} disabled={loading}>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea name="notes" className="form-textarea" rows={3} value={formData.notes} onChange={handleInputChange} placeholder="Additional notes" disabled={loading} />
          </div>

          <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
            <button className="btn btn-secondary" onClick={() => setCurrentView('list')} disabled={loading}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
              <Check size={18} /> {loading ? 'Saving...' : editingItem ? 'Update Payment' : 'Add Receipt'}
            </button>
          </div>
        </div>
      </>
    );
  }

  return renderLayout(
    <>
      <div className="page-header">
        <h1 className="page-title">Receipt / Payment History</h1>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn btn-secondary" onClick={openExport} disabled={loading}><Download size={18} /> Export</button>
          <button className="btn btn-primary" onClick={handleAdd} disabled={loading}><Plus size={18} /> Add Receipt</button>
        </div>
      </div>

      {error && <div className="error-container"><p>{error}</p><button className="btn btn-primary btn-sm" onClick={() => loadPayments(currentPage)}>Retry</button></div>}

      <div className="filter-bar">
        <div className="filter-group">
          <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>Customer</label>
          <select name="customer_id" value={filters.customer_id} onChange={e => setFilters(p => ({ ...p, customer_id: e.target.value }))} className="form-select" style={{ minWidth: '150px' }}>
            <option value="">All Customers</option>
            {sortedByName(customers).map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>Start Date</label>
          <input type="date" max={TODAY_DATE} name="start_date" value={filters.start_date} onChange={e => setFilters(p => ({ ...p, start_date: e.target.value }))} className="form-input" />
        </div>
        <div className="filter-group">
          <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>End Date</label>
          <input type="date" max={TODAY_DATE} name="end_date" value={filters.end_date} onChange={e => setFilters(p => ({ ...p, end_date: e.target.value }))} className="form-input" />
        </div>
        <div className="filter-group">
          <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>Mode</label>
          <select name="payment_mode" value={filters.payment_mode} onChange={e => setFilters(p => ({ ...p, payment_mode: e.target.value }))} className="form-select">
            <option value="">All Modes</option>
            <option value="cash">Cash</option>
            <option value="bank">Bank</option>
            <option value="upi">UPI</option>
            <option value="both">Both</option>
          </select>
        </div>
        <button className="btn btn-secondary" onClick={() => setFilters({ customer_id: '', start_date: '', end_date: '', payment_mode: '', status: '' })} style={{ height: '42px', alignSelf: 'flex-end' }}>Clear</button>
      </div>

      {!selectMode ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
          <BulkSelectToggle onClick={enterSelectMode} disabled={loading || data.length === 0} />
        </div>
      ) : (
        <BulkSelectBar
          selectedCount={selectedIds.size}
          onDelete={requestBulkDelete}
          onCancel={exitSelectMode}
          disabled={loading || bulkDeleting}
        />
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {selectMode && (
                <th className="select-cell">
                  <input type="checkbox" className="row-checkbox" checked={allSelected} onChange={toggleSelectAll} />
                </th>
              )}
              <th>ID</th>
              <th>
                <button
                  type="button"
                  onClick={() => setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', color: 'inherit', padding: 0 }}
                  title={sortOrder === 'asc' ? 'Sorted oldest first — click for newest first' : 'Sorted newest first — click for oldest first'}
                >
                  Date {sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              </th>
              <th>Customer</th>
              <th>Invoice No</th>
              <th>Amount</th>
              <th>Mode</th>
              <th>Txn / Ref</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map(item => (
              <tr key={rowKey(item)}>
                {selectMode && (
                  <td className="select-cell">
                    <input type="checkbox" className="row-checkbox" checked={selectedIds.has(rowKey(item))} onChange={() => toggleRow(rowKey(item))} />
                  </td>
                )}
                <td>#{item.payment_id}</td>
                <td>{formatDate(item.payment_date)}</td>
                <td style={{ fontWeight: 500 }}>{item.customer_name || `Customer ${item.customer_id}`}</td>
                <td>{item.invoice_number ? <span style={{ fontWeight: 600, color: 'var(--primary-color)' }}>{item.invoice_number}</span> : <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>-</span>}</td>
                <td style={{ fontWeight: 600, color: 'var(--success-color)' }}>{formatCurrency(item.amount)}</td>
                <td><span className={`mode-badge ${item.payment_mode}`}>{item.payment_mode}</span></td>
                <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{item.transaction_id || item.reference_number || '-'}</td>
                <td><span className={`status-badge ${item.status || 'completed'}`}>{item.status || 'completed'}</span></td>
                <td>
                  <div className="actions-cell">
                    <button className="btn-icon" onClick={() => handleEdit(item)} disabled={loading} title="Edit"><Edit size={18} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && !loading && <div className="no-data"><p>No payment records found</p></div>}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button className="pagination-btn" onClick={() => loadPayments(currentPage - 1)} disabled={currentPage === 1 || loading}><ChevronLeft size={16} /></button>
          <span style={{ margin: '0 1rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Page {currentPage} of {totalPages}</span>
          <button className="pagination-btn" onClick={() => loadPayments(currentPage + 1)} disabled={currentPage === totalPages || loading}><ChevronRight size={16} /></button>
        </div>
      )}

      <div style={{ marginTop: '1rem', padding: '1rem 1.2rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Total Records (current page)</div>
          <div style={{ fontWeight: 700 }}>{data.length}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Sum of Amount (current page)</div>
          <div style={{ fontWeight: 700, color: 'var(--success-color)' }}>
            {formatCurrency(data.reduce((s, p) => s + (p.amount || 0), 0))}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Total Records (filter)</div>
          <div style={{ fontWeight: 700 }}>{totalItems}</div>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Showing {data.length} of {totalItems} records</div>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete selected records?"
        message={`Delete ${confirmDelete?.count || 0} selected payment record(s)? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        loading={bulkDeleting}
        onConfirm={confirmBulkDelete}
        onCancel={() => setConfirmDelete(null)}
      />

      <ExportModal open={showExport} onClose={() => setShowExport(false)} onExport={handleExport} loading={exporting} title="Export Receipts to CSV">
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Customer</label>
          <select className="form-select" value={exportFilters.customer_id} onChange={e => setExportFilters(p => ({ ...p, customer_id: e.target.value }))}>
            <option value="">All Customers</option>
            {sortedByName(customers).map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
          </select>
        </div>
        <div className="form-row" style={{ marginBottom: 0 }}>
          <div className="form-group">
            <label className="form-label">Start Date</label>
            <input type="date" max={TODAY_DATE} className="form-input" value={exportFilters.start_date} onChange={e => setExportFilters(p => ({ ...p, start_date: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">End Date</label>
            <input type="date" max={TODAY_DATE} className="form-input" value={exportFilters.end_date} onChange={e => setExportFilters(p => ({ ...p, end_date: e.target.value }))} />
          </div>
        </div>
        <div className="form-row" style={{ marginBottom: 0 }}>
          <div className="form-group">
            <label className="form-label">Mode</label>
            <select className="form-select" value={exportFilters.payment_mode} onChange={e => setExportFilters(p => ({ ...p, payment_mode: e.target.value }))}>
              <option value="">All Modes</option>
              <option value="cash">Cash</option>
              <option value="bank">Bank</option>
              <option value="upi">UPI</option>
              <option value="both">Both</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-select" value={exportFilters.status} onChange={e => setExportFilters(p => ({ ...p, status: e.target.value }))}>
              <option value="">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>
      </ExportModal>
    </>
  );
}

export default PaymentHistoryPage;
